import { LinearProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { pdfjs } from "react-pdf";

import Modal from "@components/Modal";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export type File = {
  name: string;
  uploadedAt: string;
  createdBy: {
    id: string;
    name: string;
  };
  awsKey?: string;
  id?: string;
};

export const FILE_TYPES = {
  "Home Sleep Test Results": "HST",
  "Lab Results": "LAB&TESTS",
  "CPAP Reports": "DME",
  "General Medical Records": "OTHER",
  "Referral Paperwork": "OTHER",
  Other: "OTHER",
} as const;

export type FileTypeKey = keyof typeof FILE_TYPES;
export type FileType = (typeof FILE_TYPES)[FileTypeKey];

const Viewer = ({
  fileUrl,
  scale = 2,
}: {
  fileUrl: string;
  scale?: number;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPDF = async () => {
      const container = containerRef.current;
      if (!container) return;

      setLoading(true);
      // Clear previous renders
      container.innerHTML = "";

      const pdf = await pdfjs.getDocument(fileUrl).promise;
      const numPages = pdf.numPages;

      for (let i = 1; i <= numPages; i++) {
        // eslint-disable-next-line no-await-in-loop
        await pdf.getPage(i).then(async (page) => {
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.marginBottom = "0px";
          canvas.style.maxWidth = "100vw";
          canvas.style.height = "auto";

          if (context) {
            await page
              .render({ canvasContext: context, viewport })
              .promise.then(() => {
                container.appendChild(canvas);

                if (i !== 1 && i <= numPages) {
                  const divider = document.createElement("div");
                  divider.style.height = "6px";
                  divider.style.background = "#ccc";
                  divider.style.margin = "16px 0";
                  container.appendChild(divider);
                }
              });

            container.appendChild(canvas);
          }
        });

        setLoading(false);
      }
    };

    loadPDF();
  }, [fileUrl]);

  return (
    <div>
      {loading && (
        <LinearProgress
          sx={{
            width: "100%",
            position: "fixed",
            top: 0,
            left: 0,
          }}
        />
      )}
      <div style={{ display: "flex", justifyContent: "center", flexDirection: "column" }} ref={containerRef} />
    </div>
  );
};

type PDFViewerProps = {
  url: string;
  open: boolean;
  scale?: number;
  handleClose: () => void;
};

const PDFViewer = ({
  url = "",
  scale = 2,
  open = false,
  handleClose,
}: PDFViewerProps) => {
  return (
    <Modal
      fullScreen
      open={open}
      title=""
      bodyParagraphs={[]}
      sx={{
        "& .MuiPaper-root": {
          paddingX: 0,
        },
      }}
      buttons={[
        {
          variant: "outlined",
          label: "Close",
          onClick: () => handleClose(),
        },
      ]}
    >
      {url && <Viewer fileUrl={url} scale={scale} />}
    </Modal>
  );
};

export default PDFViewer;
